import { Stepper } from "@mantine/core";

export interface RegistrationTrackerProps {
  steps: { label: string }[];
  /** 1-based index of the current step (1 = first step). */
  activeStep: number;
}

/**
 * The active step's icon (`data-progress`) defaults to a blue outline with a
 * transparent fill. Per the design feedback we want it filled *and* outlined in
 * artcloud-blue. Mantine ships its component styles inside `@layer mantine`, so
 * this unlayered rule reliably wins regardless of source order. Pending
 * (`no data attr`) and completed (`data-completed`) steps are left untouched.
 */
const ACTIVE_STEP_STYLES = `
.mantine-Stepper-stepIcon[data-progress] {
  background-color: var(--mantine-color-artcloud-blue-6);
  border-color: var(--mantine-color-artcloud-blue-6);
  color: var(--mantine-color-white);
}`;

/**
 * Registration progress indicator built on Mantine's `Stepper`.
 *
 * `Stepper.active` is 0-based, whereas this component's public `activeStep`
 * prop is 1-based (step 1 = first step), so we translate on the way in.
 * `size`/`iconSize` drive the number-circle dimensions per the design feedback.
 *
 * Each step exposes `data-testid="registration-step-N"` and `data-active`
 * for tests and styling hooks; those props are forwarded to the step root.
 */
export function RegistrationTracker({
  steps,
  activeStep,
}: RegistrationTrackerProps) {
  const activeIndex = activeStep - 1;

  return (
    <>
      <style>{ACTIVE_STEP_STYLES}</style>
      <Stepper
        active={activeIndex}
        size="xs"
        iconSize={26}
        color="artcloud-blue"
        aria-label="Registration progress"
        styles={{
          root: { width: "100%" },
          steps: { alignItems: "center" },
          separator: { marginInline: "0.5rem" },
          stepBody: { marginInlineStart: "0.5rem" },
          stepLabel: {
            textTransform: "uppercase",
            fontSize: "11px",
            fontWeight: 500,
            letterSpacing: "0.1em",
            whiteSpace: "nowrap",
          },
        }}
      >
        {steps.map((step, index) => (
          <Stepper.Step
            key={step.label}
            label={step.label}
            data-testid={`registration-step-${index + 1}`}
            data-active={index === activeIndex ? "true" : "false"}
          />
        ))}
      </Stepper>
    </>
  );
}
